import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Botao,
  ColunaDataList,
  DataList,
  ErrorState,
  Field,
  Input,
  Modal,
  Nota,
  Skeleton,
} from '../../../ui';
import { numeroParaMoeda, parseMoeda } from '../../../utils/moeda';
import { extrairMensagemErro } from '../../../utils/apiError';
import {
  ValorCotaStatus,
  ValorCotaHistoricoItem,
  getValorCotaStatus,
  getValorCotaHistorico,
  atualizarValorCota,
  atualizarValorCotaAutomatico,
} from '../../../api/rateio/rateioApi';

/**
 * Valor do rateio por cota da competência (at-02).
 * Reescrito sobre o design system na at-67 (frente D/D5).
 *
 * 🔴 **Três correções de comportamento:**
 *
 *   1. **A falha ao carregar deixava o modal EM BRANCO.** O `catch` mostrava um
 *      toast (que passa) e o corpo ficava só com os botões — sem status, sem
 *      histórico e sem dizer por quê. Agora é `ErrorState` com "Tentar novamente".
 *   2. **Os campos da atualização manual não tinham rótulo visível.** O de
 *      competência se explicava por um `title`, que **não existe no celular**, e o
 *      de valor, só pelo `placeholder` — que some ao digitar. Agora os dois são
 *      `Field` + `Input`.
 *   3. **O valor era lido com `parseFloat(v.replace(',', '.'))`.** Digitar
 *      "1.350,00" produziria `parseFloat("1.350.00")` = **1,35** — a mesma família
 *      de defeito que a at-60 e a at-62 corrigiram no valor de adesão. Agora é o
 *      `parseMoeda` de `utils/moeda.ts`, que lê por estrutura.
 *
 * ⚠️ **Os textos dos botões NÃO mudaram** — "Buscar valor atualizado na Hinova" e
 * "Salvar" são citados entre aspas pelo `docs/manual-ti-sincronizacoes.md`, que
 * está em uso desde 19/07/2026.
 *
 * 📌 **A digitação continua livre** (`[0-9,.]`, sem `digitarMoeda`/`normalizarMoeda`):
 * é um campo de fallback usado uma vez por mês, com o valor copiado da planilha do
 * fechamento. O que mudou é só como aquela string vira número.
 */

/** `2026-09-01` → `09/2026`. Local de propósito: é competência, não data — não
 *  passa por `new Date` (que aplicaria fuso e poderia voltar um mês). */
const formatCompetencia = (iso: string) => {
  const [ano, mes] = iso.slice(0, 10).split('-');
  return `${mes}/${ano}`;
};

const COLUNAS: ColunaDataList<ValorCotaHistoricoItem>[] = [
  {
    id: 'competencia',
    rotulo: 'Competência',
    principal: true,
    celula: (item) => formatCompetencia(item.competencia),
  },
  {
    id: 'valor',
    rotulo: 'Valor',
    celula: (item) => numeroParaMoeda(item.valor),
  },
  {
    id: 'origem',
    rotulo: 'Origem',
    celula: (item) => (item.origem === 'AUTOMATICO' ? 'Automático' : 'Manual'),
  },
  {
    id: 'por',
    rotulo: 'Por',
    celula: (item) => item.atualizadoPor ?? '—',
  },
];

interface ModalRateioValorCotaProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalRateioValorCota: React.FC<ModalRateioValorCotaProps> = ({ isOpen, onClose }) => {
  const [status, setStatus] = useState<ValorCotaStatus | null>(null);
  const [historico, setHistorico] = useState<ValorCotaHistoricoItem[]>([]);
  const [novoValor, setNovoValor] = useState('');
  const [competencia, setCompetencia] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erroCarga, setErroCarga] = useState('');
  const [salvando, setSalvando] = useState<'automatico' | 'manual' | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroCarga('');
    try {
      const [st, hist] = await Promise.all([getValorCotaStatus(), getValorCotaHistorico()]);
      setStatus(st);
      setHistorico(hist);
    } catch (falha) {
      setErroCarga(extrairMensagemErro(falha, 'Erro ao carregar os dados do rateio.'));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setNovoValor('');
    setCompetencia('');
    void carregar();
  }, [isOpen, carregar]);

  const handleAtualizarAutomatico = async () => {
    setSalvando('automatico');
    try {
      const resultado = await atualizarValorCotaAutomatico();
      toast.success(
        `Valor atualizado pela Hinova: ${numeroParaMoeda(resultado.valor)} (${formatCompetencia(
          resultado.competencia,
        )})`,
      );
      await carregar();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao buscar o valor na Hinova'));
    } finally {
      setSalvando(null);
    }
  };

  const handleSalvar = async () => {
    const valorNumerico = parseMoeda(novoValor);
    if (!valorNumerico || valorNumerico <= 0) {
      toast.error('Informe um valor válido maior que zero.');
      return;
    }

    setSalvando('manual');
    try {
      await atualizarValorCota(valorNumerico, competencia || undefined);
      toast.success('Valor do rateio por cota atualizado.');
      setNovoValor('');
      setCompetencia('');
      await carregar();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao atualizar o valor'));
    } finally {
      setSalvando(null);
    }
  };

  const ocupado = salvando !== null;

  return (
    <Modal
      aberto={isOpen}
      titulo="Valor do Rateio por Cota"
      tamanho="lg"
      ocupado={ocupado}
      aoFechar={onClose}
      rodape={
        <Botao variante="secundario" onClick={onClose} disabled={ocupado}>
          Fechar
        </Botao>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-5">
        {carregando && !status && !erroCarga && <Skeleton linhas={2} altura="tw-h-12" />}

        {erroCarga && !status && (
          <ErrorState
            mensagem="Não foi possível carregar os dados do rateio."
            detalhe={erroCarga}
            aoTentarNovamente={() => void carregar()}
            ocupado={carregando}
          />
        )}

        {status && (
          <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-superficie tw-p-4">
              <p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-texto-suave">
                Em uso
              </p>
              <p className="tw-mb-1 tw-mt-1 tw-font-marca tw-text-2xl tw-font-bold tw-text-texto-titulo">
                {numeroParaMoeda(status.valorEmUso)}
              </p>
              <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
                Competência {formatCompetencia(status.competenciaDoValor)}
                {status.origem
                  ? ` · ${status.origem === 'AUTOMATICO' ? 'automático' : 'manual'}`
                  : ''}
                {status.atualizadoPor ? ` · por ${status.atualizadoPor}` : ''}
              </p>
            </div>

            {status.defasado && (
              <Nota variante="atencao">
                A competência {formatCompetencia(status.competenciaCorrente)} ainda não tem valor
                atualizado — o sistema está usando o último valor conhecido.
              </Nota>
            )}
          </div>
        )}

        <div className="tw-flex tw-flex-col tw-gap-2">
          <Botao
            variante="primario"
            larguraTotal
            carregando={salvando === 'automatico'}
            disabled={ocupado}
            onClick={handleAtualizarAutomatico}
          >
            {salvando === 'automatico'
              ? 'Consultando Hinova...'
              : 'Buscar valor atualizado na Hinova'}
          </Botao>
          <Nota>
            Consulta o valor vigente no SGA e registra para a competência corrente — sem digitar
            nada. <b>É o caminho principal.</b>
          </Nota>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-superficie-borda tw-pt-4">
          <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-texto-titulo">
            Atualização manual
          </p>
          <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
            Use apenas se a busca automática falhar — o valor vem da planilha do fechamento.
          </p>

          <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-end">
            <Field rotulo="Valor" className="sm:tw-flex-1" dica="Ex.: 16,94">
              <Input
                value={novoValor}
                inputMode="decimal"
                placeholder="16,94"
                disabled={ocupado}
                onChange={(evento) => setNovoValor(evento.target.value.replace(/[^0-9,.]/g, ''))}
              />
            </Field>

            {/* ⚠️ O rótulo era um `title` — invisível no celular, que é onde o TI
                às vezes está. Agora é `Field`. */}
            <Field
              rotulo="Competência (opcional)"
              className="sm:tw-flex-1"
              dica="Em branco = mês corrente"
            >
              <Input
                type="month"
                value={competencia}
                disabled={ocupado}
                onChange={(evento) => setCompetencia(evento.target.value)}
              />
            </Field>

            <Botao
              variante="secundario"
              carregando={salvando === 'manual'}
              disabled={ocupado}
              onClick={handleSalvar}
              className="sm:tw-mb-[1.375rem]"
            >
              Salvar
            </Botao>
          </div>
        </div>

        {historico.length > 0 && (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-border-t tw-border-superficie-borda tw-pt-4">
            <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-texto-titulo">Histórico</p>
            <DataList
              itens={historico}
              colunas={COLUNAS}
              chave={(item) => item.competencia}
              rotuloAcessivel="Histórico do valor do rateio por cota"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ModalRateioValorCota;
